import { ComponentType, FC, useContext, useEffect, useState } from 'react';

import {
  BottomIcon,
  TopIcon,
  ICommonIconProps,
  Label,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import { NavLink, useLocation } from 'react-router-dom';

import { NavItem } from 'components/NavItem';
import { LocalizationContext } from 'localization';

import { IMenuItem } from '../../interfaces';
import s from './NavGroup.module.scss';

interface INavGroupProps {
  items: readonly IMenuItem[];
  Icon: ComponentType<ICommonIconProps>;
  title: string;
  small?: boolean;
}

export const NavGroup: FC<INavGroupProps> = ({ items, Icon, title, small }) => {
  const { t } = useContext(LocalizationContext);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  const [isCollapsed, setIsCollapsed] = useState(true);

  const { pathname } = useLocation();

  const isGroupActive = items.some(({ to }) => to.split('?')[0] === pathname);

  useEffect(() => {
    if (small) {
      setIsCollapsed(true);
      return;
    }

    setIsCollapsed(!isGroupActive);
  }, [isGroupActive, small]);

  return (
    <div
      className={s.root}
      onMouseEnter={() => {
        if (!small) return;

        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        setIsTooltipVisible(true);
      }}
      onMouseLeave={(e) => {
        if (!small) return;

        if (e.pageX < 48 || e.pageX > 53) {
          setIsTooltipVisible(false);
          return;
        }

        const timeout = setTimeout(() => setIsTooltipVisible(false), 400);
        setTimeoutId(timeout);
      }}
    >
      {isTooltipVisible && (
        <div className={s.tooltip}>
          <span className={s['tooltip-title']}>{title}</span>
          <div className={s.separator} />
          {items.map((item) => (
            <NavLink
              key={item.title}
              to={item.to}
              className={({ isActive }) =>
                cn(s['tooltip-link'], isActive && s.active)
              }
            >
              {t(item.title)}
              {item.featureDisabled && (
                <Label
                  iconName="LockIcon"
                  color="violet"
                  className={s.feature}
                />
              )}
            </NavLink>
          ))}
        </div>
      )}
      <button
        data-onboarding-menu-active={String(isGroupActive)}
        onClick={(e) => {
          e.stopPropagation();
          if (small) return;

          setIsCollapsed(!isCollapsed);
        }}
        className={cn(
          s.title,
          isGroupActive && isCollapsed && s.active,
          isGroupActive && isCollapsed && s['active-text'],
          small && s.small
        )}
      >
        <Icon size="m" className={cn(s.icon, small && s.small)} />
        {!small && title}
        {!small && (
          <>{isCollapsed ? <BottomIcon size="m" /> : <TopIcon size="m" />}</>
        )}
      </button>
      {!isCollapsed && (
        <ul className={s.links}>
          {items.map((item) => (
            <li key={item.title}>
              <NavItem
                title={t(item.title)}
                to={item.to}
                featureDisabled={item.featureDisabled}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
